import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import DropZoneInput from "./../../../components/DropZoneInput";
import enTemplate from "../../../assets/templetes/Event log data import.xlsx";
import viTemplate from "../../../assets/templetes/Mẫu nhập sự kiện chấm công.xlsx";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { addEventLogBulk } from "../../../axios/Eventlog/EventLogApi";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAuth } from "../../../context/AuthContext";

dayjs.extend(utc);
dayjs.extend(timezone);

// ======================== Helpers & Constants ========================

// Regex helpers
const TZ_REGEX = /^([+-])?(\d{1,2})(?::?(\d{2}))?$/;

const toTZOffset = (val) => {
  const s = norm(val);
  if (!s) return null;            // không có => null (coi là lỗi)
  const m = s.match(TZ_REGEX);
  if (!m) return null;            // sai format => null (coi là lỗi)

  const sign = m[1];              // luôn là '+'
  const hh = String(m[2]).padStart(2, "0");
  const mm = String(m[3] || "00").padStart(2, "0");

  return `${sign}${hh}:${mm}`;    // chuẩn hóa về +HH:MM
};

const HOUR_MIN_SEC = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
// const DATE_DMY = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/; // (không dùng trực tiếp, giữ lại nếu cần)

// Map theo key đã slug
const EVENT_TYPE_MAP = {
  checkin: 1,
  checkout: 2,
  chamcongvao: 1,
  vao: 1,
  chamcongra: 2,
  ra: 2,
};

// Bắt buộc phải có các cột này trong header (canonical)
const REQUIRED_HEADERS = ["email", "date", "time", "timezone", "shift", "event"];
// Nếu muốn cho phép thiếu timezone (mặc định +07:00), xóa "timezone" khỏi mảng trên.

// Tạo chuỗi ddMMyyyyHHmmss
const timeStamp = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(
    now.getHours()
  )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

// slug
const slug = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// Alias header → canonical
const HEADER_ALIASES = {
  email: "email",
  useremail: "email",
  // date
  date: "date",
  ngay: "date",
  ngaysukien: "date",
  // time
  time: "time",
  thoigian: "time",
  gio: "time",
  giophutgiay: "time",
  // timezone
  timezone: "timezone",
  muigio: "timezone",
  // shift
  shift: "shift",
  calamviec: "shift",
  ca: "shift",
  // description
  description: "description",
  mota: "description",
  ghichu: "description",
  // event
  event: "event",
  sukien: "event",
};

const readHeaderRow = (ws) => {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
  const headerRow = rows?.[0] || [];
  const canonical = headerRow
    .map((h) => HEADER_ALIASES[slug(h)] || null)
    .filter(Boolean);
  return { headerRow, canonical };
};

const mapRowToCanonical = (row) => {
  const out = {};
  Object.keys(row).forEach((rawKey) => {
    const key = HEADER_ALIASES[slug(rawKey)];
    if (key && out[key] === undefined) {
      out[key] = row[rawKey];
    }
  });
  return out;
};

const norm = (v) => (v ?? "").toString().trim();
const pad2 = (n) => String(n).padStart(2, "0");

const toYYYYMMDD = (val, { forceDMY = true } = {}) => {
  const isValidDateParts = (year, month, day) => {
    if (!year || !month || !day) return false;
    if (month < 1 || month > 12) return false;
    const daysInMonth = new Date(year, month, 0).getDate(); // month là 1-12
    return day >= 1 && day <= daysInMonth;
  };

  // Trường hợp là Date object
  if (val instanceof Date && !isNaN(val)) {
    const yyyy = val.getFullYear();
    const mm = pad2(val.getMonth() + 1);
    const dd = pad2(val.getDate());
    if (!isValidDateParts(yyyy, Number(mm), Number(dd))) return null;
    return `${yyyy}-${mm}-${dd}`;
  }

  // Trường hợp là số (date code của Excel)
  if (typeof val === "number") {
    try {
      const d = XLSX.SSF.parse_date_code(val); // { y, m, d, ... }
      const year = d.y;
      const month = d.m;
      const day = d.d;
      if (!isValidDateParts(year, month, day)) return null;

      const yyyy = String(year).padStart(4, "0");
      const mm = pad2(month);
      const dd = pad2(day);
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return null;
    }
  }

  // Trường hợp là string
  const s = (val ?? "").toString().trim();
  if (!s) return null;

  // Đã đúng dạng yyyy-MM-dd thì vẫn nên validate lại
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [yyyyStr, mmStr, ddStr] = s.split("-");
    const yyyy = Number(yyyyStr);
    const mm = Number(mmStr);
    const dd = Number(ddStr);
    if (!isValidDateParts(yyyy, mm, dd)) return null;
    return s;
  }

  // Các dạng dd/MM/yyyy, d/M/yy, dd-MM-yyyy, ...
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    let day = parseInt(m[1], 10);
    let month = parseInt(m[2], 10);
    let yearStr = m[3];

    // Nếu không force DMY thì xử lý mơ hồ (US vs EU), bạn đang forceDMY = true
    if (!forceDMY) {
      if (day <= 12 && month > 12) [day, month] = [month, day];
    }

    // Chuẩn hóa năm 2 chữ số
    if (yearStr.length === 2) {
      const yy = parseInt(yearStr, 10);
      yearStr = (yy >= 70 ? "19" : "20") + yearStr;
    }

    const year = Number(yearStr);

    // 👉 Validate ngày thật sự tồn tại (ví dụ 31/11/2025 sẽ fail ở đây)
    if (!isValidDateParts(year, month, day)) return null;

    return `${yearStr}-${pad2(month)}-${pad2(day)}`;
  }

  // Không match format nào
  return null;
};


const toHHMMSS = (val) => {
  if (typeof val === "number") {
    const totalSec = Math.round(val * 24 * 60 * 60);
    const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
    const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
    const ss = String(totalSec % 60).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
  const s = norm(val);
  if (!s) return null;
  const m = s.match(HOUR_MIN_SEC);
  if (m) {
    const hh = String(m[1]).padStart(2, "0");
    const mm = String(m[2]).padStart(2, "0");
    const ss = String(m[3] ?? "00").padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
  return null;
};

const ImportDataDialog = ({ onClose, usersData = [], shiftsData = [], onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({ fileImport: null });
  const [parsing, setParsing] = useState(false);
  const isDialogLoading = parsing || isSubmitting;
  const [errors, setErrors] = useState([]);
  const [reportRows, setReportRows] = useState([]); // lưu báo cáo để tải lại

  const { genaralSettingTimeZone } = useAuth();

  const resetFileImport = () => {
    setFormData((prev) => ({
      ...prev,
      fileImport: null,
    }));
  };

  const tr = (key, fb) => t(key) || fb;

  const downloadReportXlsx = ({ rows = [], sourceFileName = "" }) => {
    const headerEmail = tr("import.report.email", "email");
    const headerStatus = tr("import.report.status", "trạng thái");
    const headerReason = tr("import.report.reason", "lý do");
    const sheetName = tr("import.report.sheet", "Kết quả");

    const aoa = [
      [headerEmail, headerStatus, headerReason],
      ...rows.map((r) => [r.email || "", r.status || "", r.reason || ""]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 30 }, { wch: 16 }, { wch: 80 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const fileBaseName = tr("import.report.file-name", "import-report");

    const fname = `${fileBaseName}_${timeStamp()}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const eventTypeFromText = (v) => {
    const key = slug(v);
    return EVENT_TYPE_MAP[key] ?? null;
  };

  const handleFileChange = (name, file) => {
    setFormData((prev) => ({ ...prev, [name]: file }));
    setErrors([]);
    setParsing(false);
    setReportRows([]); // reset
  };

  const handleInputChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0] || null;
    setFormData((prev) => ({ ...prev, [name]: file }));
    setErrors([]);
    setParsing(false);
    setReportRows([]); // reset
  };

  const handleDownloadTemplate = () => {
    const lang = i18n.language?.toLowerCase() || "en";
    const isVI = lang.startsWith("vi");
    const url = isVI ? viTemplate : enTemplate;

    const a = document.createElement("a");
    a.href = url;
    a.download = isVI ? "Mẫu nhập sự kiện chấm công.xlsx" : "Event log data import.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const buildAttendanceDateTimeUTC = (date, time, tzOffset) => {
    if (!date || !time || !tzOffset) return null;

    // Ghép datetime + offset, ví dụ: 2026-01-15 11:00:00 +07:00
    const isoLike = `${date} ${time} ${tzOffset}`;

    const d = dayjs(isoLike, "YYYY-MM-DD HH:mm:ss Z");

    if (!d.isValid()) return null;

    return d.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
  };


  const handleConfirm = async () => {
    const file = formData.fileImport;
    if (!file) {
      return;
    }

    setParsing(true);
    const newErrors = [];

    try {
      // Chuẩn bị map nhanh
      const emailToUserId = new Map(
        (usersData || [])
          .map((u) => {
            const e1 = norm(u?.email).toLowerCase();
            const e2 = norm(u?.userEmail).toLowerCase();
            const id = Number(u?.userId ?? u?.id ?? null);
            return e1 ? [e1, id] : e2 ? [e2, id] : [null, null];
          })
          .filter(([k, v]) => k && v)
      );

      const shiftIdSet = new Set(
        (shiftsData || []).map((sh) => Number(sh?.id ?? sh?.shiftId)).filter(Boolean)
      );
      const nameOrCodeToShiftId = new Map(
        (shiftsData || []).flatMap((sh) => {
          const id = Number(sh?.id ?? sh?.shiftId ?? null);
          const name = norm(sh?.name ?? sh?.shiftName).toLowerCase();
          const code = norm(sh?.code ?? sh?.shiftCode).toLowerCase();
          const arr = [];
          if (id) {
            if (name) arr.push([name, id]);
            if (code) arr.push([code, id]);
          }
          return arr;
        })
      );

      const findUserIdByEmailFast = (email) =>
        emailToUserId.get((email || "").toLowerCase()) || null;

      // const findShiftIdFast = (val) => {
      //   const s = norm(val).toLowerCase();
      //   if (!s) return null;
      //   if (/^\d+$/.test(s)) {
      //     const id = Number(s);
      //     return shiftIdSet.has(id) ? id : null;
      //   }
      //   return nameOrCodeToShiftId.get(s) || null;
      // };

      const findShiftIdFast = (val) => {
        const s = norm(val).toLowerCase();
        if (!s) return null;

        // Nếu người dùng gõ "default" / "mặc định" → coi như không chọn ca, dùng default
        if (["default", "macdinh", "mặc định"].includes(s)) {
          return 1;
        }

        if (/^\d+$/.test(s)) {
          const id = Number(s);
          return shiftIdSet.has(id) ? id : null;
        }
        return nameOrCodeToShiftId.get(s) || null;
      };


      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });

      const dataSheetName = wb.SheetNames.find((name) => {
        if (!name) return false;
        return name.toLowerCase() === "data" || name.toLowerCase() === "dữ liệu";
      });


      if (!dataSheetName) {
        const msg =
          tr("import.no-data-sheet", "Không tìm thấy sheet tên 'data' trong file.");
        setErrors([msg]);

        const rows = [
          {
            email: "",
            status: tr("import.status.fail", "Thất bại"),
            reason: msg,
          },
        ];
        setReportRows(rows);
        downloadReportXlsx({ sourceFileName: file.name, rows });
        resetFileImport();
        toast.error(tr("common.add-event-log-fail", "Thêm nhật ký thất bại"));
        setParsing(false);
        return;
      }

      const ws = wb.Sheets[dataSheetName];
      if (!ws) {
        const msg =
          tr("import.no-data-sheet", "Không tìm thấy sheet tên 'data' trong file.");
        setErrors([msg]);

        const rows = [
          {
            email: "",
            status: tr("import.status.fail", "Thất bại"),
            reason: msg,
          },
        ];
        setReportRows(rows);
        downloadReportXlsx({ sourceFileName: file.name, rows });
        resetFileImport();
        toast.error(tr("common.add-event-log-fail", "Thêm nhật ký thất bại"));
        setParsing(false);
        return;
      }

      // Check header trước
      const { canonical } = readHeaderRow(ws);
      const missing = REQUIRED_HEADERS.filter((need) => !canonical.includes(need));
      if (missing.length > 0) {
        const humanize = {
          email: tr("import.header.email", "Email"),
          date: tr("import.header.date", "Date"),
          time: tr("import.header.time", "Time"),
          timezone: tr("import.header.timezone", "Timezone"),
          shift: tr("import.header.shift", "Shift"),
          event: tr("import.header.event", "Event"),
        };
        const missList = missing.map((k) => humanize[k] || k).join(", ");
        const errs = [
          `${tr("import.error.missing-headers", "Thiếu cột bắt buộc")}: ${missList}`,
        ];
        setErrors(errs);

        const rows = [
          {
            email: "",
            status: tr("import.status.fail", "Thất bại"),
            reason: errs.join("; "),
          },
        ];
        setReportRows(rows);
        downloadReportXlsx({ sourceFileName: file.name, rows });
        resetFileImport();
        toast.error(tr("common.add-event-log-fail", "Thêm nhật ký thất bại"));
        setParsing(false);
        return;
      }

      // Đọc data rows
      const rows = XLSX.utils.sheet_to_json(ws, {
        raw: false,
        defval: "",
        dateNF: "dd/mm/yyyy",
      });

      const payloads = [];
      const rejected = [];
      const reports = []; // báo cáo excel

      rows.forEach((row, idx) => {
        const obj = mapRowToCanonical(row);
        const line = idx + 2;

        const email = norm(obj["email"]);
        const date = toYYYYMMDD(obj["date"], { forceDMY: true });
        const time = toHHMMSS(obj["time"]);
        const tz = toTZOffset(obj["timezone"]);
        // const shift = findShiftIdFast(obj["shift"]);
        const rawShift = obj["shift"];
        const normShift = norm(rawShift).toLowerCase();

        // Mặc định
        let shift = findShiftIdFast(rawShift);
        let isDefaultShift = false;

        // Nếu người dùng gõ "default" / "macdinh" / "mặc định" → coi là dùng ca mặc định
        if (["default", "macdinh", "mặc định"].includes(normShift)) {
          isDefaultShift = true;
        }
        const description = norm(obj["description"]);
        const eventType = eventTypeFromText(obj["event"]);
        const userId = email ? findUserIdByEmailFast(email) : null;

        const errs = [];
        if (!email) {
          errs.push(tr("import.error.missing-email", "Thiếu email"));
        }

        if (email && !userId) {
          errs.push(
            tr("import.error.user-not-found", "Không tìm thấy người dùng") + ` (${email})`
          );
        }
        if (!date) errs.push(tr("import.error.invalid-date", "Ngày không hợp lệ"));
        if (!time) errs.push(tr("import.error.invalid-time", "Giờ không hợp lệ"));
        if (!shift) errs.push(tr("import.error.invalid-shift", "Ca làm việc không hợp lệ"));
        if (!eventType) errs.push(tr("import.error.invalid-event", "Sự kiện không hợp lệ"));
        if (!tz) errs.push(
          tr(
            "import.error.invalid-timezone",
            "Múi giờ không hợp lệ (định dạng hợp lệ ví dụ: +07:00)"
          )
        );

        const attendanceDateTime = buildAttendanceDateTimeUTC(date, time, tz);

        if (errs.length) {
          newErrors.push(`${tr("import.error.row", "Dòng")} ${line}: ${errs.join("; ")}`);
          rejected.push({
            line,
            email,
            shift: obj["shift"],
            event: obj["event"],
            errs,
          });
          // Báo cáo: thất bại + lý do
          reports.push({
            email: email || "",
            status: tr("import.status.fail", "Thất bại"),
            reason: errs.join("; "),
          });
        } else {
          payloads.push({
            userId,
            shift,
            description,
            eventType,
            attendanceDateTime,
            timeZone: tz,
            langId: localStorage.getItem("i18nextLngId"),
            latitude: "",
            longitude: "",
            useDifferentShiftDate: false,
            shiftDate: format(new Date(), "yyyy-MM-dd"),
          });
          // Báo cáo: thành công
          reports.push({
            email: email || "",
            status: tr("import.status.success", "Thành công"),
            reason: "",
          });
        }
      });

      setErrors(newErrors);

      // Xuất báo cáo Excel (toàn bộ dòng)
      setReportRows(reports);
      if (newErrors.length > 0) {
        downloadReportXlsx({ sourceFileName: file.name, rows: reports });
        resetFileImport();
        toast.error(tr("common.add-event-log-fail", "Thêm nhật ký thất bại"));
      }
      const result = {
        ok: newErrors.length === 0 && payloads.length > 0,
        payloads,
        errors: newErrors,
        rejected,
        totalRows: rows.length,
        validRows: payloads.length,
        fileName: file.name,
      };

      // Nếu dữ liệu hợp lệ thì gọi API
      if (result.ok) {
        try {
          setIsSubmitting(true);
          const res = await addEventLogBulk(payloads);

          if (res?.data?.responseCode === 0) {
            toast.success(tr("common.add-event-log-successfully", "Thêm nhật ký thành công"));
            onRefresh?.();
            onClose();
          } else {
            toast.error(res?.data?.message || "Error");
          }
        } catch (error) {
          let msg = error?.response?.data?.message || error?.message || "Error";
          const match = String(msg).match(/{.*}/);
          if (match) {
            try {
              msg = JSON.parse(match[0])?.message || msg;
            } catch { }
          }
          toast.error(msg);
        } finally {
          setIsSubmitting(false);
        }
      }
    } catch (e) {
      console.error(e);
      const msg = tr("import.error.cannot-read-file", "Không thể đọc file.");
      setErrors([msg]);
      const rows = [{ email: "", status: tr("import.status.fail", "Thất bại"), reason: msg }];
      setReportRows(rows);
      downloadReportXlsx({
        sourceFileName: formData.fileImport?.name || "",
        rows,
      });
      resetFileImport();
      toast.error(tr("common.add-event-log-fail", "Thêm nhật ký thất bại"));

    } finally {
      setParsing(false);
    }
  };

  return (
    <CustomDialog
      transitionDuration={300}
      maxWidth={"xl"}
      open
      loading={isDialogLoading}
      onClose={onClose}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("button.import-data")}</p>
          </div>
          <GrayIconButton onClick={onClose} disabled={isDialogLoading}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="px-1 pt-4">
          <div className="grid grid-cols-12 gap-4">
            <label className="col-span-12 text-sm font-medium">
              {t("common.choose-file")}
            </label>
            <div className="col-span-12">
              <DropZoneInput
                className="w-full"
                file={formData.fileImport}
                handleInputChange={handleInputChange}
                handleDropChange={handleFileChange}
                name="fileImport"
              />
            </div>
          </div>

          <div className="mt-3 text-sm text-gray-600">
            {t("common.need-sample") || "Need sample?"}{" "}
            <button
              type="button"
              className="text-blue-600 font-medium underline underline-offset-2"
              onClick={handleDownloadTemplate}
            >
              {t("common.download-template-here") || "Download template here"}
            </button>
          </div>

          {errors.length > 0 && (
            <>

              <div className="mt-2">
                <button
                  type="button"
                  className="text-blue-600 font-medium underline underline-offset-2"
                  onClick={() =>
                    downloadReportXlsx({
                      sourceFileName: formData.fileImport?.name || "",
                      rows: reportRows,
                    })
                  }
                >
                  {t("import.download-error-file") || "Tải báo cáo (Excel)"}
                </button>
              </div>
            </>
          )}

        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={onClose} disabled={isDialogLoading}>
            <CloseIcon />
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            disabled={!formData.fileImport || isDialogLoading}
            onClick={handleConfirm}
          >
            <PlayArrowIcon />
            <p>{t("button.execute")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default ImportDataDialog;
